"use client";

// These files are the single source of truth for BOTH distribution channels
// of the Magnus chat UI (npm package + shadcn registry). Edit here; never
// fork. This component is a faithful port of `@magnus/react`'s
// `chat/transcript/message-response.tsx` so packaged consumers render
// streamed markdown + <ref> chiclets identically to the Magnus web app.
import { c as _c } from "react/compiler-runtime";
import { cn } from "@workspace/ui/lib/utils";
import { type ComponentProps, createContext, memo, type ReactNode, use, useEffect } from "react";
import remarkBreaks from "remark-breaks";
import { defaultRemarkPlugins, parseMarkdownIntoBlocks, Streamdown } from "streamdown";
import { ChicletRenderer } from "./chiclets";
import { useSmoothText } from "./use-smooth-text";
const REF_ALLOWED_TAGS = {
  ref: ["type", "refid", "label"]
};
const LINK_SAFETY = {
  enabled: false
} as const;

/**
 * Lets a host draw some links in assistant text as something richer than a
 * link — e.g. a file the model linked by its sandbox path, which no browser
 * can open, as a download card. Return `undefined` to keep the plain link.
 * Called during render, so it must not use hooks; return an element instead.
 */
export type MessageLinkRenderer = (link: {
  children: ReactNode;
  href: string;
}) => ReactNode | undefined;
const MessageLinkContext = createContext<MessageLinkRenderer | null>(null);
export const MessageLinkProvider = MessageLinkContext.Provider;
const INCOMPLETE_LINK = "streamdown:incomplete-link";

/** Streamdown's own anchor, plus the host's {@link MessageLinkRenderer}. */
function MessageLink(t0) {
  const $ = _c(17);
  let children;
  let className;
  let href;
  let rest;
  if ($[0] !== t0) {
    const {
      children: t1,
      className: t2,
      href: t3,
      node: _node,
      ...t4
    } = t0;
    children = t1;
    className = t2;
    href = t3;
    rest = t4;
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = rest;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    rest = $[4];
  }
  const renderLink = use(MessageLinkContext);
  let t1;
  if ($[5] !== children || $[6] !== href || $[7] !== renderLink) {
    t1 = href && href !== INCOMPLETE_LINK && renderLink ? renderLink({
      children,
      href
    }) : undefined;
    $[5] = children;
    $[6] = href;
    $[7] = renderLink;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const custom = t1;
  if (custom !== undefined) {
    return custom;
  }
  let t2;
  if ($[9] !== className) {
    t2 = cn("wrap-anywhere font-medium text-primary underline", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = href === INCOMPLETE_LINK;
  let t4;
  if ($[11] !== children || $[12] !== href || $[13] !== rest || $[14] !== t2 || $[15] !== t3) {
    t4 = <a className={t2} data-incomplete={t3} data-streamdown="link" href={href} rel="noreferrer" target="_blank" {...rest}>{children}</a>;
    $[11] = children;
    $[12] = href;
    $[13] = rest;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
const MESSAGE_COMPONENTS = {
  ref: ChicletRenderer,
  a: MessageLink
};
type MarkdownNode = {
  children?: MarkdownNode[];
  type: string;
  url?: string;
};
const SANDBOX_SCHEME = /^sandbox:\/+/;
function stripSandboxScheme(node: MarkdownNode) {
  if ((node.type === "link" || node.type === "definition") && node.url) {
    node.url = node.url.replace(SANDBOX_SCHEME, "/");
  }
  for (const child of node.children ?? []) {
    stripSandboxScheme(child);
  }
}

/**
 * Models link files they wrote as `sandbox:/path` — a scheme no browser opens,
 * which link hardening renders as "[blocked]". Down to the bare path, it
 * reaches the {@link MessageLinkRenderer}, which can offer the file itself.
 */
function remarkSandboxLinks() {
  return stripSandboxScheme;
}
const REMARK_PLUGINS = [...Object.values(defaultRemarkPlugins), remarkSandboxLinks];

/**
 * remark plugins for messages that should treat a single `\n` as a visible
 * line break (chat-input convention) rather than a CommonMark soft break.
 * Extends — not replaces — the defaults above, then appends
 * `remark-breaks` (which only affects soft breaks inside text, never
 * structural newlines in code fences, lists, tables, or blockquotes).
 */
const HARD_BREAK_REMARK_PLUGINS = [...REMARK_PLUGINS, remarkBreaks];

/** Block that is only whitespace and a single self-closing <ref> tag (inline ref chiclet). */
const REF_ONLY_BLOCK = /^\s*<ref\s[\s\S]*?\/>\s*$/;

/**
 * Like Streamdown's default block splitter, but merges any block that is only
 * a <ref /> tag with the previous and next blocks. This keeps
 * "text <ref /> more text" in one block so the chiclet and surrounding text
 * render on the same line.
 */
function parseMarkdownIntoBlocksWithInlineRefs(markdown: string): string[] {
  const blocks = parseMarkdownIntoBlocks(markdown);
  if (blocks.length <= 1) {
    return blocks;
  }
  const merged: string[] = [];
  let i = 0;
  while (i < blocks.length) {
    const curr = blocks[i] ?? "";
    const next = blocks[i + 1];
    if (REF_ONLY_BLOCK.test(curr) && i + 1 < blocks.length && next !== undefined) {
      if (merged.length > 0) {
        merged[merged.length - 1] += curr + next;
      } else {
        merged.push(curr + next);
      }
      i += 2;
      continue;
    }
    if (REF_ONLY_BLOCK.test(curr) && merged.length > 0) {
      merged[merged.length - 1] += curr;
      i += 1;
      continue;
    }
    merged.push(curr);
    i += 1;
  }
  return merged;
}

/*
 * Streamdown's table toolbar finds the table it acts on by walking *up* the DOM
 * for a `[data-streamdown="table-wrapper"]` ancestor. Its fullscreen view
 * renders a second toolbar and table into a `createPortal(..., document.body)`
 * overlay that it never tags as a wrapper, so from there the lookup finds
 * nothing and copy/download silently no-op (the handlers report via an
 * `onError` prop the fullscreen toolbar doesn't pass). Feedback
 * KBM00MxqKJOJF5tQXBmYo.
 *
 * Tagging the overlay's own container restores the lookup. Upstream fixed this
 * identically in streamdown 2.6.0, which we can't take yet — it moves mermaid
 * and Shiki highlighting into separate plugin packages and adds default
 * table/code scroll caps. The `hasAttribute` guard makes this a no-op on any
 * build that tags the overlay itself, so it retires quietly on that upgrade.
 */
const STREAMDOWN_ATTR = "data-streamdown";
const FULLSCREEN_OVERLAY = `[${STREAMDOWN_ATTR}="table-fullscreen"]`;
const TABLE_WRAPPER = "table-wrapper";
function tagOverlayAsWrapper(overlay: Element) {
  const container = overlay.firstElementChild;
  if (container && !container.hasAttribute(STREAMDOWN_ATTR)) {
    container.setAttribute(STREAMDOWN_ATTR, TABLE_WRAPPER);
  }
}
function tagAddedOverlays(records: MutationRecord[]) {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) {
        continue;
      }
      if (node.matches(FULLSCREEN_OVERLAY)) {
        tagOverlayAsWrapper(node);
        continue;
      }
      for (const overlay of node.querySelectorAll(FULLSCREEN_OVERLAY)) {
        tagOverlayAsWrapper(overlay);
      }
    }
  }
}

/*
 * One observer for the whole transcript: every rendered message calls this, but
 * the overlay always mounts as a direct child of `document.body`, so a single
 * non-recursive `childList` watch covers all of them. Scoping it to body's own
 * children (no `subtree`) is what keeps it off the hot path — streaming markdown
 * mutates the transcript constantly, and none of that reaches this observer.
 */
let overlayObserver: MutationObserver | null = null;
let overlayObserverConsumers = 0;
function useFullscreenTableExportFix() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  overlayObserverConsumers = overlayObserverConsumers + 1;
  if (!overlayObserver) {
    overlayObserver = new MutationObserver(tagAddedOverlays);
    overlayObserver.observe(document.body, {
      childList: true
    });
  }
  return _temp;
}
function _temp() {
  overlayObserverConsumers = overlayObserverConsumers - 1;
  if (overlayObserverConsumers === 0) {
    overlayObserver?.disconnect();
    overlayObserver = null;
  }
}
export type MessageResponseProps = {
  children?: string;
  className?: string;
  /**
   * When true, a single `\n` renders as a visible line break instead of a
   * CommonMark soft break. Set for user messages (chat-input convention);
   * left off for assistant text, where LLM-authored markdown deliberately
   * uses single `\n` as a soft break.
   */
  hardLineBreaks?: boolean;
};
const streamdownProps = {
  className: "",
  allowedTags: REF_ALLOWED_TAGS,
  components: MESSAGE_COMPONENTS,
  linkSafety: LINK_SAFETY,
  parseMarkdownIntoBlocksFn: parseMarkdownIntoBlocksWithInlineRefs
};
export const MessageResponse = memo(t0 => {
  const $ = _c(6);
  const {
    className,
    children,
    hardLineBreaks: t1
  } = t0;
  const hardLineBreaks = t1 === undefined ? false : t1;
  useFullscreenTableExportFix();
  let t2;
  if ($[0] !== className) {
    t2 = cn("w-full min-w-0 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:leading-7", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = hardLineBreaks ? HARD_BREAK_REMARK_PLUGINS : REMARK_PLUGINS;
  let t4;
  if ($[2] !== children || $[3] !== t2 || $[4] !== t3) {
    t4 = <Streamdown {...streamdownProps} className={t2} remarkPlugins={t3}>{children}</Streamdown>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}, (prevProps, nextProps) => prevProps.children === nextProps.children && prevProps.hardLineBreaks === nextProps.hardLineBreaks);
MessageResponse.displayName = "MessageResponse";

/**
 * Assistant text with the typing animation applied (plan 024, gap-audit
 * T10). `streaming` drives {@link useSmoothText}: pass `false` to bypass
 * the animation so already-seen text snaps in (e.g. while replaying a
 * buffered reattach) instead of re-streaming character by character.
 */
export function SmoothTextBlock(t0) {
  const $ = _c(3);
  const {
    hardLineBreaks: t1,
    streaming,
    text
  } = t0;
  const hardLineBreaks = t1 === undefined ? false : t1;
  const smoothed = useSmoothText(text, streaming);
  let t2;
  if ($[0] !== hardLineBreaks || $[1] !== smoothed) {
    t2 = <MessageResponse hardLineBreaks={hardLineBreaks}>{smoothed}</MessageResponse>;
    $[0] = hardLineBreaks;
    $[1] = smoothed;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}